/**
 * Update seller-order status — what the seller (or admin via tools) posts to
 * `POST /api/orders/update-status` to advance an order through the seller
 * lifecycle (pending → processing → shipped → delivered) or to a terminal
 * state (cancelled, refunded).
 *
 * The boundary parse covers shape only: `newStatus` must be one of the six
 * canonical states. The forward-only transition rules (and refund-side-effect
 * gating for the cancelled/refunded transitions) stay in the route handler
 * because they depend on the current row's status.
 *
 * Mobile-consumed (api-orders.ts updateOrderStatus). Schema is additive — every
 * status value mobile already sends is preserved in the enum.
 *
 * `reason` (optional, ≤500 chars) — DECIDE_REFUND_PLAN_SPEC.md §7 Q4 (reason
 * parity, LOCKED same-PR as the `handleFullCancellation` decideRefundPlan
 * rewire). Threaded into `refund_attempts.reason` on the full-cancel path
 * (mirrors `cancel-item`'s `order_items.cancellation_reason`). Additive-only:
 * absent → `refund_attempts.reason` stays `null` (today's behaviour,
 * unchanged for callers that don't send it).
 *
 * @see app/api/orders/update-status/route.ts — handler + state-machine rules
 * @see apps/mobile/lib/api-orders.ts updateOrderStatus — mobile caller
 * @see .tondocs/payments/API_REQUEST_BOUNDARY_AUDIT.md H-9
 * @see .tondocs/payments/DECIDE_REFUND_PLAN_SPEC.md §7 Q4
 */

import { z } from "zod";
import { pinned } from "./pin";

/**
 * Canonical seller-order status set. Mirrors `apps/mobile/lib/api-orders.ts`
 * `OrderStatus` and the values the route handler branches on.
 *
 * Kept as a `z.enum` so adding a future state requires touching exactly this
 * declaration plus the downstream switches that branch on it.
 */
export const SELLER_ORDER_STATUSES = [
  "pending",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
  "refunded",
] as const;
export type SellerOrderStatus = (typeof SELLER_ORDER_STATUSES)[number];

export interface UpdateOrderStatusRequest {
  /** `seller_orders.id` to advance. */
  sellerOrderId: string;
  /** Target status. Transition validity is enforced server-side. */
  newStatus: SellerOrderStatus;
  /**
   * Optional cancellation note. Only consumed on the `newStatus:"cancelled"`
   * full-cancel path — stored on `refund_attempts.reason` (§7 Q4 parity with
   * `cancel-item`'s `order_items.cancellation_reason`). Max 500 chars to keep
   * email templates predictable (mirrors `CancelItemRequest.reason`).
   */
  reason?: string;
}

export const UpdateOrderStatusRequestSchema = pinned<UpdateOrderStatusRequest>()(
  z.object({
    sellerOrderId: z.string().min(1),
    newStatus: z.enum(SELLER_ORDER_STATUSES),
    reason: z.string().max(500).optional(),
  }),
);

/**
 * Success response — union of the route's four success shapes (plain
 * transition, idempotent already-cancelled re-entry, all-items-cancelled,
 * and the full-cancel outcome with refund/manual/stock facts).
 *
 * ⚠️ `success: true` can arrive with **HTTP 500**: the route deliberately
 * returns 500 when the refund succeeded but the stock restore failed
 * (`stockRestoreFailed: true`) so partial failures stay red in logs. Clients
 * MUST key success off the parsed body, never off `res.ok` — branching on
 * the HTTP status alone reports "refund issued" as an error to the seller
 * while the buyer's money has already moved (mobile did exactly that until
 * 2026-08-02).
 */
export interface UpdateOrderStatusManualRefundContext {
  sellerCaptureId: string | null;
  paypalOrderId: string;
  amount: number;
  currency: string;
  dashboardUrl: string;
  refundDeadline: string | null;
}

export interface UpdateOrderStatusRefundResult {
  refundId: string;
  /** Provider/ledger status (e.g. COMPLETED, PENDING); null on the
   * already-cancelled re-read when `refund_money_state` was never set. */
  status: string | null;
  /** Total refunded to the buyer (items + shipping + service fee). Absent on
   * the already-cancelled re-entry shape. */
  amount?: number;
  usedFallback?: boolean;
}

export interface UpdateOrderStatusSuccessResponse {
  success: true;
  status: SellerOrderStatus;
  /** Idempotent re-entry: the order was already cancelled, no new money moved. */
  alreadyCancelled?: boolean;
  refund?: UpdateOrderStatusRefundResult;
  /** Legacy order without a linked capture — the seller must refund by hand. */
  manualRefundRequired?: boolean;
  manualRefundContext?: UpdateOrderStatusManualRefundContext | null;
  stockRestoreFailed?: boolean;
}

export const UpdateOrderStatusSuccessResponseSchema =
  pinned<UpdateOrderStatusSuccessResponse>()(
    z.object({
      success: z.literal(true),
      status: z.enum(SELLER_ORDER_STATUSES),
      alreadyCancelled: z.boolean().optional(),
      refund: z
        .object({
          refundId: z.string(),
          status: z.string().nullable(),
          amount: z.number().optional(),
          usedFallback: z.boolean().optional(),
        })
        .optional(),
      manualRefundRequired: z.boolean().optional(),
      manualRefundContext: z
        .object({
          sellerCaptureId: z.string().nullable(),
          paypalOrderId: z.string(),
          amount: z.number(),
          currency: z.string(),
          dashboardUrl: z.string(),
          refundDeadline: z.string().nullable(),
        })
        .nullable()
        .optional(),
      stockRestoreFailed: z.boolean().optional(),
    }),
  );
